import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getActiveCompanyId } from '../../../lib/services/company.service';
import {
  buildUpdateCatalogFormatTemplate,
  getFormatTemplateStageLibrary,
} from '../../../lib/services/format-template.service';
import {
  buildStagesFromStageLibrary,
  stripDerivedStageConfigKeys,
} from '../../../lib/helpers/format-template.helpers';
import { usePendingSubmits } from '../../_core/Formik/use-pending-submits';
import { useStateWithRef } from '../../_core/Formik/use-state-with-ref';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from '../format-template.messages';
import { useFormatTemplateReferenceData } from '../use-format-template-stages';
import { useFormatTemplateDiagramUpload } from '../use-format-template-diagram-upload';
import { EditCatalogTemplateModal } from './edit-catalog-template-modal.component';

interface IEditCatalogTemplateModalContainerProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  template: IFormatTemplate | null;
}

// Admin editor for a composed (CATALOG) family: stage settings and band diagrams only — the
// structure (stages, days, groups) is seed-owned and has no edit surface here by design.
export const EditCatalogTemplateModalContainer: React.FC<IEditCatalogTemplateModalContainerProps> = ({
  open,
  onClose,
  onSaved,
  template,
}) => {
  const companyId = getActiveCompanyId();
  // The heavy per-definition configs load on open only; the list fragment never carries them.
  const { stageLibrary, loading: libraryLoading } = getFormatTemplateStageLibrary(
    template?._id,
    companyId || undefined,
    { skip: !open },
  );
  // A SYSTEM family is composed by every association, so a company-owned drill/plan id saved
  // here would resolve for nobody else — offer globals only.
  const { drillOptions, practicePlanOptions, positionOptions, loading: referenceLoading } =
    useFormatTemplateReferenceData(template, open, template?.scope === 'SYSTEM');
  const { updateCatalogFormatTemplate } = buildUpdateCatalogFormatTemplate();
  const { pendingDiagrams, uploadingKey, uploadError, selectDiagram, resetDiagrams } =
    useFormatTemplateDiagramUpload(template);

  // Ref half lets Save read stage edits flushed a moment earlier synchronously (see useStateWithRef).
  const [editedConfigs, setEditedConfigs, editedConfigsRef] = useStateWithRef<{ [ref: string]: object }>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<React.ReactNode>(null);
  const { register: registerStageSubmit, flush: flushStageSubmits, clear: clearStageSubmits } = usePendingSubmits();

  // Reset per template/open so a reopened editor never shows another session's buffered edits.
  useEffect(() => {
    setEditedConfigs({});
    clearStageSubmits();
    resetDiagrams();
    setError(null);
  }, [template, open, setEditedConfigs, clearStageSubmits, resetDiagrams]);

  // The library plus the edit overlay is the single source of stage rows — no reseed effect, so
  // a late cache-and-network refresh can never clobber configs the admin already changed.
  const stages = useMemo(() => {
    const base = buildStagesFromStageLibrary(stageLibrary);
    return base.map(s => (editedConfigs[s._id] ? { ...s, config: editedConfigs[s._id] } : s));
  }, [stageLibrary, editedConfigs]);

  const handleUpdateStage = useCallback((id: string, changes: { config?: object; title?: string }) => {
    if (!changes.config) return;
    setEditedConfigs(prev => ({ ...prev, [id]: changes.config as object }));
  }, [setEditedConfigs]);

  // Starting a new upload clears the previous save error, mirroring the pre-hook behavior.
  const handleSelectDiagram = useCallback((bandKey: string, file: File) => {
    setError(null);
    return selectDiagram(file, bandKey);
  }, [selectDiagram]);

  const handleSave = useCallback(async () => {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      // Flush dirty stage forms into the ref before reading it (see usePendingSubmits).
      await flushStageSubmits();

      const typeByRef = new Map(stageLibrary.map(d => [d.key, d.type]));
      // The backend refuses configs carrying per-band derived keys, so they are dropped here.
      const stageSettings = Object.entries(editedConfigsRef.current).map(([ref, config]) => ({
        ref,
        config: stripDerivedStageConfigKeys(typeByRef.get(ref) ?? '', config as { [key: string]: unknown }),
      }));
      const bandDiagrams = Object.entries(pendingDiagrams).map(([bandKey, diagram]) => ({
        bandKey,
        imagePath: diagram.imagePath,
      }));

      if (!stageSettings.length && !bandDiagrams.length) {
        onClose();
        return;
      }

      await updateCatalogFormatTemplate(template._id, {
        ...(stageSettings.length && { stageSettings }),
        ...(bandDiagrams.length && { bandDiagrams }),
      });
      onSaved();
    } catch (err) {
      console.error('updateFormatTemplate (catalog settings) failed:', err);
      setError(<IntlMsg {...messages.catalogSaveError} />);
    } finally {
      setSaving(false);
    }
  }, [template, stageLibrary, editedConfigsRef, pendingDiagrams, flushStageSubmits, updateCatalogFormatTemplate, onSaved, onClose]);

  if (!template) return null;

  return (
    <EditCatalogTemplateModal
      open={open}
      onClose={onClose}
      template={template}
      stageLibrary={stageLibrary}
      stages={stages}
      updateStage={handleUpdateStage}
      registerStageSubmit={registerStageSubmit}
      drillOptions={drillOptions}
      practicePlanOptions={practicePlanOptions}
      positionOptions={positionOptions}
      stagesLoading={libraryLoading || referenceLoading}
      pendingDiagrams={pendingDiagrams}
      uploadingBandKey={uploadingKey}
      onSelectDiagram={handleSelectDiagram}
      onSave={handleSave}
      saving={saving}
      error={error ?? uploadError}
    />
  );
};
